import { normalizeIndicatorName } from "./Indices";

export interface IndicatorLimitedTest {
  allowedIndicatorDefinitions?: Array<{ name?: string | null } | null> | null;
}

export interface IndicatorHeader {
  name: string;
}

/**
 * The set of indicator definition names (normalised with `normalizeIndicatorName`)
 * that the report may show, or `null` when no filtering should be applied.
 *
 * `allowedIndicatorDefinitions` is null on a test when no customer limit applies to it,
 * so as soon as any test in the report is unlimited every column stays visible (the
 * limited tests simply show N/A in columns they are not allowed to see, because the
 * backend already withholds those results). When every test is limited the union of
 * their allow-lists is used.
 */
export const computeAllowedIndicatorNames = (
  tests: IndicatorLimitedTest[]
): Set<string> | null => {
  if (tests.length === 0) {
    return null;
  }

  const names = new Set<string>();
  for (const test of tests) {
    const allowed = test.allowedIndicatorDefinitions;
    if (!allowed) {
      return null;
    }

    for (const definition of allowed) {
      const name = normalizeIndicatorName(definition?.name);
      if (name) {
        names.add(name);
      }
    }
  }

  return names;
};

export const isIndicatorAllowed = (
  indicatorName: string,
  allowedIndicatorNames: Set<string> | null
): boolean =>
  allowedIndicatorNames === null ||
  allowedIndicatorNames.has(normalizeIndicatorName(indicatorName));

/** Keeps only the hardcoded page headers whose indicator the report may show. */
export const filterIndicatorHeaders = <H extends IndicatorHeader>(
  headers: readonly H[],
  allowedIndicatorNames: Set<string> | null
): H[] =>
  allowedIndicatorNames === null
    ? [...headers]
    : headers.filter((header) =>
        isIndicatorAllowed(header.name, allowedIndicatorNames)
      );
